// The developer zone's sections (zones/developers/zone.json "sections"; docs/design/briefs/developers.md). Every pitch
// line is a claims-ledger line (docs/launch/claims.md). The speed lines and numbers come only from launch-copy.ts,
// which test/launch-copy.test.ts holds to docs/launch/developer-page.md word for word: this file never types a number
// or a speed word, it parses and lays out what the copy already says. The game names sit behind the `owner-names`
// gate (on since the owner's OK, claims S10); unset, the beats read "a driving game", "a flight game" and so on.
//
// The CLI path (H1–H3, P4) and the template download (H4's zip) are blocked in the ledger: each step waits on its
// fact with the copy's own gates and shows on a dev server with ?held=1 (pages/developers.ts showHeld).

import { getCatalogue } from "../../catalogue.ts";
import { tableView, type RatioRow } from "../../charts.ts";
import { h, link } from "../../dom.ts";
import { DEV_BLOCKS, DEV_BLOCKS_AFTER, DEV_FAQ, DEV_GET, DEV_HERO, DEV_NUMBERS_AFTER, DEV_NUMBERS_FOOT, DEV_TABLE, fill, GUIDE, HAUNT_RULES, plain, type Block } from "../../launch-copy.ts";
import { CREATOR_GETS, dashCopy } from "../../pages/dashboard.ts";
import { actionLinks, block, ctxWith, line, listedCount, passes, type Ctx } from "../../pages/developers.ts";
import { selling } from "../../selling.ts";
import { TEMPLATE_ART } from "../open-haunt/pack.ts";
import { reducedMotion } from "../../tokens.ts";
import { getIcon, pathLine, whyScene } from "./art.ts";
import { BENCH_ART, BENCH_COPY, HAUNT_OFFER, PATH_STEPS, TEMPLATE_COPY, WAYS_COPY } from "./copy.ts";

/** "1.39×" → 1.39 (the copy's own rounding, never re-rounded). */
export function ratioOf(text: string): number {
  const m = /(\d+(?:\.\d+)?)\s*×/.exec(text);
  return m ? Number(m[1]) : NaN;
}

/** The benchmark rows as the chart draws them: label through the names gate, the copy's ratio and raw values. */
export function benchRows(c: Ctx): RatioRow[] {
  const [, ours, base] = DEV_TABLE.head;
  return DEV_TABLE.rows.map(([label, a, b, ratio]) => ({
    label: fill(label!, c.facts, c.held) ?? "",
    ratio: ratioOf(ratio!),
    text: ratio!,
    detail: `${ours} ${a}, ${base} ${b}`,
  }));
}

/** The two averages from the summary line ("1.12× … 1.07× …"), labelled by the table's own row kinds. */
export function summaryRows(c: Ctx): RatioRow[] {
  const nums = [...DEV_NUMBERS_AFTER[0]!.matchAll(/(\d+\.\d+)×/g)].map((m) => m[1]!);
  const kinds = benchRows(c).map((r) => r.label.slice(r.label.lastIndexOf(", ") + 2));
  const rate = kinds.find((k) => /rate/.test(k)) ?? "";
  const load = kinds.find((k) => /load/.test(k)) ?? "";
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return [rate, load].map((k, i) => ({ label: cap(k), ratio: Number(nums[i]), text: `${nums[i]}×` })).filter((r) => r.label && Number.isFinite(r.ratio));
}

const ctx = (): Ctx => ctxWith({});

// ---- hook: the claims that matter, each with its scope ----

export function hookSection(): HTMLElement {
  const c = ctx();
  const avg = DEV_NUMBERS_AFTER[0]!;
  const today = DEV_BLOCKS[0]!;
  const tile = (big: string, el: HTMLElement | null, cls = "") => (el ? h("li", { class: `forge-claim ${cls}` }, h("span", { class: "forge-big", "aria-hidden": "true" }, big), el) : null);
  return h(
    "section",
    { class: "forge forge-hook" },
    h(
      "ol",
      { class: "forge-claims", role: "list" },
      tile(`${summaryRows(c)[0]?.text ?? ""}`, line("p", avg, c), "lead"),
      today.paras?.[0] ? h("li", { class: "forge-claim today" }, h("span", { class: "forge-big small" }, today.heading ?? ""), line("p", today.paras[0], c)) : null,
    ),
    line("p", DEV_HERO.sub, c, { class: "forge-foot" }),
    // the brief's one primary action is getting on Banger (ember); the numbers link is the secondary
    actionLinks([...DEV_HERO.actions].sort((a, b) => Number(a.href !== "#get") - Number(b.href !== "#get")), c),
  );
}

// ---- benchmark: the two averages, then one game per beat, each in its own art; the table on request ----

/** One game's rows from the table (frame rate or time, then load), keyed by its name with names on. */
export interface BenchGame {
  key: string;
  name: string;
  rows: RatioRow[];
}

export function benchGames(c: Ctx): BenchGame[] {
  const games: BenchGame[] = [];
  const shown = benchRows(c);
  DEV_TABLE.rows.forEach(([label], i) => {
    const key = plain(label!).slice(0, plain(label!).lastIndexOf(", "));
    const row = shown[i]!;
    const at = games.find((g) => g.key === key);
    if (at) at.rows.push(row);
    else games.push({ key, name: row.label.slice(0, row.label.lastIndexOf(", ")), rows: [row] });
  });
  return games;
}

/** Elements that animate in when they scroll into view: `.in` is the final state, so without the observer, under
 * reduced motion or in a headless capture every bar and number is drawn as it ends. */
function revealOnScroll(root: HTMLElement): void {
  const all = () => root.querySelectorAll<HTMLElement>(".reveal");
  const done = (el: HTMLElement) => {
    el.classList.add("in");
    el.querySelectorAll<HTMLElement>("[data-count]").forEach(countUp);
  };
  if (typeof IntersectionObserver === "undefined" || reducedMotion() || navigator.webdriver) {
    queueMicrotask(() => all().forEach((el) => el.classList.add("in")));
    return;
  }
  root.classList.add("animate");
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) if (e.isIntersecting) (io.unobserve(e.target), done(e.target as HTMLElement));
    },
    { rootMargin: "0px 0px -18% 0px", threshold: 0.15 },
  );
  queueMicrotask(() => all().forEach((el) => io.observe(el)));
}

/** "1.30×" counts up from 1.00× to the copy's own text, which it always ends on. */
function countUp(el: HTMLElement): void {
  const end = el.dataset.count ?? "";
  const to = ratioOf(end);
  if (!Number.isFinite(to)) return;
  const t0 = performance.now();
  const ms = 1100;
  const step = (now: number) => {
    const k = Math.min(1, (now - t0) / ms);
    const v = 1 + (to - 1) * (1 - Math.pow(1 - k, 3));
    el.textContent = k < 1 ? `${v.toFixed(2)}×` : end;
    if (k < 1) requestAnimationFrame(step);
  };
  el.textContent = "1.00×";
  requestAnimationFrame(step);
}

/** Two bars for one row, drawn to the values themselves against the larger: a time (ms, s) is shorter for the faster
 * side, a frame rate longer. */
function benchBars(r: RatioRow, i: number): HTMLElement {
  const [, ours, base] = DEV_TABLE.head;
  const [a, b] = (r.detail ?? "").replace(`${ours} `, "").split(`, ${base} `);
  const [x, y] = [parseFloat(a ?? ""), parseFloat(b ?? "")];
  const time = /\d\s*(ms|s)$/.test(a ?? "");
  const top = Math.max(x, y) || 1;
  const kind = r.label.slice(r.label.lastIndexOf(", ") + 2);
  const bar = (who: string, value: string | undefined, w: number, cls: string) =>
    h("div", { class: `bench-bar ${cls}` }, h("span", { class: "bench-who" }, who), h("span", { class: "bench-track" }, h("span", { class: "bench-fill", style: `--w: ${((w / top) * 100).toFixed(1)}%` })), h("span", { class: "bench-val" }, value ?? ""));
  return h(
    "div",
    { class: `bench-metric ${r.ratio >= 1 ? "ahead" : "behind"}`, style: `--d: ${180 + i * 160}ms`, "aria-label": `${r.label}: ${r.text} (${r.detail})` },
    h(
      "div",
      { class: "bench-metric-head", "aria-hidden": "true" },
      h("span", null, kind.charAt(0).toUpperCase() + kind.slice(1), time ? h("em", null, BENCH_COPY.lower) : null),
      h("strong", null, r.text),
    ),
    h("div", { "aria-hidden": "true" }, bar(ours!, a, x, "ours"), bar(base!, b, y, "base")),
  );
}

function benchCard(g: BenchGame, c: Ctx, i: number): HTMLElement {
  const art = BENCH_ART[g.key];
  const lead = g.rows[0]!;
  // the hero's measured line belongs to its own game's beat (Nightrider)
  const hero = g.key === "Nightrider" ? line("p", DEV_HERO.title, c, { class: "bench-line" }) : null;
  return h(
    "li",
    { class: `bench-game reveal${art?.art ? "" : " no-art"}`, style: `--tint: ${art?.tint ?? "var(--z-accent)"}` },
    h(
      "figure",
      { class: "bench-art", "aria-hidden": "true" },
      art?.art ? h("img", { src: art.art, alt: "", loading: "lazy", decoding: "async" }) : h("span", { class: "bench-card-name" }, g.name),
    ),
    h(
      "div",
      { class: "bench-body" },
      h("span", { class: "bench-index", "aria-hidden": "true" }, String(i + 1).padStart(2, "0")),
      art?.logo ? h("h3", { class: "bench-logo" }, h("img", { src: art.logo, alt: g.name, loading: "lazy", decoding: "async" })) : h("h3", { class: "bench-name" }, g.name),
      h("p", { class: "bench-ratio" }, h("span", { "data-count": lead.text }, lead.text), h("small", null, `${BENCH_COPY.vs} ${DEV_TABLE.head[2]}`)),
      hero,
      g.rows.map((r, k) => benchBars(r, k)),
    ),
  );
}

export function benchmarkSection(): HTMLElement {
  const c = ctx();
  const head = DEV_BLOCKS[1]!;
  const avgs = summaryRows(c);
  const sec: HTMLElement = h(
    "section",
    { class: "forge forge-bench", id: "numbers" },
    h("h2", { class: "zone-h2" }, head.heading ?? ""),
    line("p", head.paras![0]!, c, { class: "forge-sub" }),
    h(
      "div",
      { class: "bench-avgs reveal", role: "group", "aria-label": plain(DEV_NUMBERS_AFTER[0]!) },
      avgs.map((r, i) =>
        h("div", { class: "bench-avg", style: `--d: ${i * 160}ms`, "aria-hidden": "true" }, h("span", { class: "bench-avg-num", "data-count": r.text }, r.text), h("span", { class: "bench-avg-label" }, r.label)),
      ),
    ),
    h("ol", { class: "bench-games", role: "list" }, benchGames(c).map((g, i) => benchCard(g, c, i))),
    line("p", DEV_NUMBERS_AFTER[1]!, c, { class: "bench-after reveal" }),
    tableView(
      DEV_TABLE.head.map((t, i) => (i === 3 ? "×" : t)),
      DEV_TABLE.rows.map((r) => [fill(r[0]!, c.facts, c.held) ?? "", ...r.slice(1)]),
      BENCH_COPY.table,
    ),
    line("p", DEV_NUMBERS_FOOT, c, { class: "forge-caption" }),
  );
  revealOnScroll(sec);
  return sec;
}

// ---- why: the rest of the developer page, one card each: a drawing, the heading, the lead line; the rest on request ----

/** A copy block as a card: its scene on top, the heading and first paragraph shown, everything after in "More". */
function whyCard(b: Block, c: Ctx, i: number): HTMLElement | null {
  const el = block(b, c);
  if (!el) return null;
  const [head, lead, ...rest] = [...el.children] as HTMLElement[];
  el.className = "why-card reveal";
  el.setAttribute("style", `--d: ${(i % 2) * 120}ms`);
  el.replaceChildren(
    h("div", { class: "why-art" }, whyScene(b.id)),
    h("div", { class: "why-text" }, head ?? null, lead ?? null, rest.length ? h("details", { class: "why-more" }, h("summary", null, WAYS_COPY.more), rest) : null),
  );
  return el;
}

export function whySection(): HTMLElement | null {
  const c = ctx();
  // a block whose lead line is held goes too, so its heading never claims it ("Free hosting" waits on the builds domain)
  const cards = DEV_BLOCKS_AFTER.filter((b) => !PATH_STEPS.some((p) => p.copy === b.id) && (!b.paras?.[0] || !!fill(b.paras[0], c.facts, c.held)))
    .map((b, i) => whyCard(b, c, i))
    .filter((b): b is HTMLElement => !!b);
  if (!cards.length) return null;
  const sec = h("section", { class: "forge forge-why" }, h("div", { class: "why-grid" }, cards));
  revealOnScroll(sec);
  return sec;
}

// ---- path: from your folder to Banger, one copyable command block per step ----

function commandBlock(lines: string[]): HTMLElement {
  const text = lines.join("\n");
  const btn: HTMLButtonElement = h(
    "button",
    {
      type: "button",
      class: "forge-copy",
      onclick: () => {
        void navigator.clipboard?.writeText(text).then(
          () => {
            btn.textContent = WAYS_COPY.copied;
            window.setTimeout(() => (btn.textContent = WAYS_COPY.copy), 1600);
          },
          () => {},
        );
      },
    },
    WAYS_COPY.copy,
  );
  return h("div", { class: "forge-cmd" }, h("pre", null, h("code", null, text)), btn);
}

/** A guide code block by what it contains (the guide's order can change). */
function guideCode(has: string): string[] | null {
  return GUIDE.find((b) => b.code?.some((l) => l.includes(has)))?.code ?? null;
}

export function pathSection(): HTMLElement | null {
  const c = ctx();
  const steps = PATH_STEPS.filter((s) => passes(s.when, c))
    .map((s) => {
      const code = s.code ? guideCode(s.code) : null;
      const text = s.copy ? DEV_BLOCKS_AFTER.find((b) => b.id === s.copy)?.paras?.[0] : s.body;
      const body = text ? line("p", text, c) : null;
      if (!body && !code) return null;
      return h("li", { class: "forge-step reveal" }, h("h3", null, s.title), body, code ? commandBlock(code.map((l) => fill(l, c.facts, c.held) ?? l)) : null);
    })
    .filter((s): s is HTMLLIElement => !!s);
  if (!steps.length) return null;
  // one step left (the CLI steps held): no numbers for a path of one, just the folder going to Banger
  const sec = h(
    "section",
    { class: `forge forge-path${steps.length === 1 ? " single" : ""}` },
    h(
      "div",
      { class: "path-head reveal" },
      h("h2", { class: "zone-h2" }, steps.length === 1 ? PATH_STEPS[0]!.title : WAYS_COPY.pathTitle),
      h("div", { class: "path-picture", "aria-hidden": "true" }, pathLine(), h("img", { src: "/mark/banger-mark.svg", alt: "", class: "path-mark" })),
    ),
    steps.length === 1 ? h("div", { class: "path-one reveal" }, steps[0]!.querySelector("p")) : h("ol", { class: "forge-steps", role: "list" }, steps),
  );
  revealOnScroll(sec);
  return sec;
}

// ---- template: The Haunt ----

export function templateSection(): HTMLElement {
  const c = ctx();
  const get = GUIDE.find((b) => b.code?.includes("npm run dev"));
  const download = get?.paras?.[0] ? fill(get.paras[0], c.facts, c.held) : null;
  const zip = download ? (/(?:https:\/\/)?banger\.sh\/\S+\.zip/.exec(download)?.[0] ?? null) : null;
  const licence = HAUNT_RULES.flatMap((r) => r.list).find((l) => l.startsWith("Free to use in free or paid games"));
  const sec = h(
    "section",
    { class: "forge forge-template reveal" },
    h("figure", { class: "forge-poster" }, h("img", { src: TEMPLATE_ART, alt: TEMPLATE_COPY.alt, loading: "lazy", decoding: "async" })),
    h(
      "div",
      { class: "forge-template-text" },
      h("p", { class: "zone-eyebrow" }, TEMPLATE_COPY.eyebrow),
      h("h2", { class: "zone-h2" }, TEMPLATE_COPY.title),
      h("p", null, TEMPLATE_COPY.line),
      licence ? line("p", licence, c, { class: "forge-dim" }) : null,
      // the commands wait on the download, like the guide (H4: the zip is hosted by prod)
      zip && get?.code ? commandBlock(get.code) : null,
      h(
        "div",
        { class: "copy-actions" },
        zip ? h("a", { class: "btn", href: zip.startsWith("http") ? zip : `https://${zip}` }, TEMPLATE_COPY.download) : null,
        link("/haunt", { class: `btn${zip ? " ghost" : ""}` }, TEMPLATE_COPY.haunt),
      ),
    ),
  );
  // the section is its own reveal: watch it from a wrapper the observer can see
  const wrap = h("div", { class: "forge-template-wrap" }, sec);
  revealOnScroll(wrap);
  return wrap;
}

// ---- ways: the signed-out "For creators" landing, merged (fixes track's creatorLanding) ----

export function waysSection(): HTMLElement {
  const c = ctx();
  const next = `/signin?next=${encodeURIComponent("/dashboard")}`;
  const offers = h(
    "div",
    { class: "forge-offers" },
    // the haunt offer uses the Forge's own dates line (HAUNT_OFFER: ship by 6 Nov, row until 10 Nov)
    [...DEV_GET.filter((o) => o.action.href !== "/haunt"), HAUNT_OFFER]
      .filter((o) => passes(o.when, c) && !o.para.includes("{{catalogue"))
      .map((o) => {
        const p = line("p", o.para, c);
        return p ? h("div", { class: "forge-offer reveal" }, p, actionLinks([o.action], c, false)) : null;
      })
      .filter(Boolean),
  );
  void getCatalogue()
    .then((cat) => {
      const n = listedCount(cat.games);
      const p = n ? line("p", DEV_GET[0]!.para, ctxWith({ "catalogue: listed count": n.toLocaleString("en-GB") })) : null;
      if (p) offers.prepend(h("div", { class: "forge-offer in" }, p, actionLinks([DEV_GET[0]!.action], c, false)));
    })
    .catch(() => {});
  const sec = h(
    "section",
    { class: "forge forge-ways", id: "get" },
    h(
      "div",
      { class: "ways-head reveal" },
      h("p", { class: "zone-eyebrow" }, WAYS_COPY.eyebrow),
      h("h2", { class: "zone-h2" }, WAYS_COPY.title),
      h("p", { class: "forge-sub" }, dashCopy(selling()).lead, " ", WAYS_COPY.age),
    ),
    h(
      "ul",
      { class: "forge-gets", role: "list" },
      CREATOR_GETS.map(([t, d], i) => h("li", { class: "reveal", style: `--d: ${i * 90}ms` }, getIcon(i), h("strong", null, t), h("span", null, d))),
    ),
    offers,
    h(
      "div",
      { class: "forge-signin reveal" },
      h("p", null, WAYS_COPY.signin),
      h("div", { class: "copy-actions" }, link(next, { class: "btn" }, WAYS_COPY.start)),
    ),
    h(
      "details",
      { class: "forge-faq" },
      h("summary", null, WAYS_COPY.questions),
      h("dl", null, DEV_FAQ.map(([q, a]) => h("div", null, line("dt", q, c), line("dd", a, c)))),
    ),
  );
  revealOnScroll(sec);
  return sec;
}
